// src/admin/pages/sold-vehicles/saleReceiptGenerator.ts
// Printable sale receipt/invoice for a sold vehicle — the "particular
// information" a real car sale needs a paper trail for for. Reuses the same
// jsPDF setup as reportPDFGenerator.ts for a consistent look.
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Product } from '../../../enums/product';
import { CompanyProfile } from '../company/companyService';
import { getCurrencySymbol } from '../../../util/productUtils';

const COLORS = {
  primary: '#1976d2',
  primaryDark: '#0d47a1',
  text: '#212121',
  muted: '#6b7280',
  border: '#e0e0e0',
  panelBg: '#f7f9fc',
  successBg: '#eaf6ea',
  successText: '#2e7d32',
};

const fmtAmt = (n: number, sym: string): string =>
  `${sym} ${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Small caps-style section label with a colored left accent bar — used to
// visually separate the receipt into scannable blocks instead of one long
// stream of plain tables.
const sectionLabel = (doc: jsPDF, text: string, x: number, y: number) => {
  doc.setFillColor(COLORS.primary);
  doc.rect(x, y - 3.2, 1.4, 4.2, 'F');
  doc.setTextColor(COLORS.text);
  doc.setFontSize(10.5);
  doc.setFont('helvetica', 'bold');
  doc.text(text.toUpperCase(), x + 4, y);
};

export class SaleReceiptGenerator {
  static generate(car: Product, company: CompanyProfile | null): void {
    const sym = getCurrencySymbol(company?.currency || 'USD');
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = 20;

    // ── Header band ──
    doc.setFillColor(COLORS.primary);
    doc.rect(0, 0, pw, 34, 'F');
    doc.setFillColor(COLORS.primaryDark);
    doc.rect(0, 34, pw, 1.2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(17); doc.setFont('helvetica', 'bold');
    doc.text(company?.companyName || 'Vehicle Sale Receipt', margin, 15);
    doc.setFontSize(9); doc.setFont('helvetica', 'normal');
    if (company?.address) {
      const addrLine = [company.address.street, company.address.city, company.address.country]
        .filter(Boolean).join(', ');
      if (addrLine) doc.text(addrLine, margin, 22);
    }
    if (company?.contactInfo?.phone || company?.contactInfo?.email) {
      doc.text(
        [company.contactInfo?.phone, company.contactInfo?.email].filter(Boolean).join('  ·  '),
        margin, 28
      );
    }
    // Receipt badge, top-right of the header band
    doc.setFontSize(10); doc.setFont('helvetica', 'bold');
    doc.text('SALE RECEIPT', pw - margin, 15, { align: 'right' });
    doc.setFontSize(8); doc.setFont('helvetica', 'normal');
    doc.text(`Ref: ${(car.sku || car.id || '').toString().slice(-10).toUpperCase()}`, pw - margin, 21, { align: 'right' });
    doc.text(`Date: ${car.soldAt ? new Date(car.soldAt as any).toLocaleDateString() : '-'}`, pw - margin, 26, { align: 'right' });

    y = 46;

    // ── Vehicle & Buyer info — side-by-side panels ──
    sectionLabel(doc, 'Vehicle & Buyer', margin, y);
    y += 5;

    const colWidth = (pw - margin * 2 - 6) / 2;
    const vehicleRows: [string, string][] = [
      ['Vehicle', car.name || '-'],
      ['VIN / Chassis', car.sku || '-'],
      ['Condition', car.condition || '-'],
      ['Sold By', car.soldByName || '-'],
    ];
    const buyerRows: [string, string][] = [
      ['Name', car.buyerName || '-'],
      ['Phone', car.buyerPhone || '-'],
      ['Email', car.buyerEmail || '-'],
      ['ID No.', car.buyerIdNumber || '-'],
    ];

    autoTable(doc, {
      startY: y,
      theme: 'plain',
      margin: { left: margin, right: pw - margin - colWidth },
      tableWidth: colWidth,
      styles: { fontSize: 8.5, cellPadding: { top: 1.6, bottom: 1.6, left: 0, right: 2 }, textColor: COLORS.text },
      columnStyles: { 0: { fontStyle: 'bold', textColor: COLORS.muted, cellWidth: 26 } },
      body: vehicleRows,
    });
    const vehicleEndY = (doc as any).lastAutoTable.finalY;

    autoTable(doc, {
      startY: y,
      theme: 'plain',
      margin: { left: margin + colWidth + 6, right: margin },
      tableWidth: colWidth,
      styles: { fontSize: 8.5, cellPadding: { top: 1.6, bottom: 1.6, left: 0, right: 2 }, textColor: COLORS.text },
      columnStyles: { 0: { fontStyle: 'bold', textColor: COLORS.muted, cellWidth: 20 } },
      body: buyerRows,
    });
    const buyerEndY = (doc as any).lastAutoTable.finalY;

    y = Math.max(vehicleEndY, buyerEndY) + 4;
    doc.setDrawColor(COLORS.border);
    doc.line(margin, y, pw - margin, y);
    y += 8;

    // ── Payment breakdown ──
    sectionLabel(doc, 'Payment Breakdown', margin, y);
    y += 5;

    const tradeInValue = car.tradeInValue || 0;
    const deposit = car.depositAmount || 0;
    const finalPrice = car.finalSalePrice || 0;
    const balanceDue = Math.max(0, finalPrice - deposit - tradeInValue);

    const rows: [string, string][] = [
      ['Final Sale Price', fmtAmt(finalPrice, sym)],
      ['Deposit Paid', fmtAmt(deposit, sym)],
    ];
    if (tradeInValue > 0) rows.push(['Trade-In Credit', fmtAmt(tradeInValue, sym)]);
    rows.push(['Balance Due', fmtAmt(balanceDue, sym)]);
    rows.push(['Payment Method', car.paymentMethod || '-']);
    if (car.paymentMethod === 'Financing') {
      rows.push(['Financing Provider', car.financingProvider || '-']);
      rows.push(['Loan Term', car.financingTermMonths ? `${car.financingTermMonths} months` : '-']);
      rows.push(['Monthly Installment', car.monthlyInstallment ? fmtAmt(car.monthlyInstallment, sym) : '-']);
    }
    const balanceDueRowIndex = rows.findIndex(r => r[0] === 'Balance Due');

    autoTable(doc, {
      startY: y,
      theme: 'grid',
      headStyles: { fillColor: COLORS.primary, fontSize: 9, halign: 'left' },
      styles: { fontSize: 9, cellPadding: 3, lineColor: COLORS.border, lineWidth: 0.2 },
      alternateRowStyles: { fillColor: COLORS.panelBg },
      columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
      head: [['Item', 'Value']],
      body: rows,
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === balanceDueRowIndex) {
          data.cell.styles.fillColor = balanceDue > 0 ? '#fdecea' : COLORS.successBg;
          data.cell.styles.textColor = balanceDue > 0 ? '#c62828' : COLORS.successText;
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    // ── Delivery & registration ──
    sectionLabel(doc, 'Delivery & Registration', margin, y);
    y += 5;
    autoTable(doc, {
      startY: y,
      theme: 'plain',
      styles: { fontSize: 8.5, cellPadding: { top: 1.6, bottom: 1.6, left: 0, right: 2 }, textColor: COLORS.text },
      columnStyles: { 0: { fontStyle: 'bold', textColor: COLORS.muted, cellWidth: 45 } },
      body: [
        ['Delivery Method', car.deliveryMethod || '-'],
        ['Delivery Date', car.deliveryDate || '-'],
        ['Registration Transfer', car.registrationTransferStatus || 'Not Started'],
        ['Warranty', car.warrantyMonths ? `${car.warrantyMonths} months${car.warrantyNotes ? ` — ${car.warrantyNotes}` : ''}` : 'None'],
      ],
    });
    y = (doc as any).lastAutoTable.finalY + 8;

    if (car.saleNotes) {
      sectionLabel(doc, 'Notes', margin, y);
      y += 5;
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(COLORS.text);
      const lines = doc.splitTextToSize(car.saleNotes, pw - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 5;
    }

    // ── Signatures ──
    y = Math.max(y + 6, doc.internal.pageSize.getHeight() - 40);
    doc.setDrawColor(COLORS.border);
    doc.line(margin, y, margin + 70, y);
    doc.line(pw - margin - 70, y, pw - margin, y);
    doc.setFontSize(8); doc.setTextColor(COLORS.muted);
    doc.text('Buyer Signature', margin, y + 5);
    doc.text('Authorized Signature', pw - margin - 70, y + 5);

    // ── Footer ──
    const pageH = doc.internal.pageSize.getHeight();
    doc.setDrawColor(COLORS.border);
    doc.line(margin, pageH - 14, pw - margin, pageH - 14);
    doc.setFontSize(7); doc.setTextColor(COLORS.muted);
    doc.text(
      company?.invoiceFooter || 'This receipt confirms the sale transaction detailed above.',
      pw / 2, pageH - 9, { align: 'center' }
    );

    doc.save(`Sale-Receipt-${car.sku || car.id}.pdf`);
  }
}
